/**
 * Procedural "general staff map" painted with Canvas2D for the menu background: paper, terrain
 * tints, engraved coastline, contour lines, rivers, a faint hex grid, two territories with a
 * toothed front line, operation arrows, cities and a few unit counters.
 *
 * It is the fallback when the PixiJS attract renderer is unavailable, and the underlay while it
 * boots. Pure function of (size, seed) so it can be regenerated on resize.
 */
import { Rng, type UnitType } from '@friction/engine';
import { ART_H, ART_W, type Tone, unitArt } from '../../render/atlas/unitArt';

// ---- noise ---------------------------------------------------------------------------------------

function perlin(seed: number) {
  const rng = new Rng(seed);
  const p = rng.shuffle(Array.from({ length: 256 }, (_, i) => i));
  const perm = new Uint8Array(512);
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
  const grad = (h: number, x: number, y: number) => {
    const g = h & 7;
    const u = g < 4 ? x : y;
    const v = g < 4 ? y : x;
    return (g & 1 ? -u : u) + (g & 2 ? -2 * v : 2 * v);
  };
  const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  return (x: number, y: number): number => {
    const xi = Math.floor(x) & 255;
    const yi = Math.floor(y) & 255;
    const xf = x - Math.floor(x);
    const yf = y - Math.floor(y);
    const u = fade(xf);
    const v = fade(yf);
    const aa = perm[perm[xi] + yi];
    const ab = perm[perm[xi] + yi + 1];
    const ba = perm[perm[xi + 1] + yi];
    const bb = perm[perm[xi + 1] + yi + 1];
    return (
      lerp(
        lerp(grad(aa, xf, yf), grad(ba, xf - 1, yf), u),
        lerp(grad(ab, xf, yf - 1), grad(bb, xf - 1, yf - 1), u),
        v,
      ) * 0.35
    );
  };
}

function fbm(n: (x: number, y: number) => number, x: number, y: number, oct = 5): number {
  let sum = 0;
  let amp = 0.5;
  let f = 1;
  let norm = 0;
  for (let o = 0; o < oct; o++) {
    sum += amp * n(x * f, y * f);
    norm += amp;
    amp *= 0.5;
    f *= 2.03;
  }
  return 0.5 + sum / norm;
}

// ---- marching squares ---------------------------------------------------------------------------

/** Segment table: for each case, pairs of edges (0 top, 1 right, 2 bottom, 3 left). */
const CASES: number[][] = [
  [],
  [3, 2],
  [2, 1],
  [3, 1],
  [0, 1],
  [3, 0, 2, 1],
  [0, 2],
  [3, 0],
  [3, 0],
  [0, 2],
  [0, 1, 3, 2],
  [0, 1],
  [3, 1],
  [2, 1],
  [3, 2],
  [],
];

function traceContour(
  ctx: CanvasRenderingContext2D,
  field: Float32Array,
  cols: number,
  rows: number,
  cs: number,
  level: number,
): void {
  ctx.beginPath();
  for (let j = 0; j < rows - 1; j++) {
    for (let i = 0; i < cols - 1; i++) {
      const a = field[j * cols + i];
      const b = field[j * cols + i + 1];
      const c = field[(j + 1) * cols + i + 1];
      const d = field[(j + 1) * cols + i];
      const idx = (a > level ? 8 : 0) | (b > level ? 4 : 0) | (c > level ? 2 : 0) | (d > level ? 1 : 0);
      const segs = CASES[idx];
      if (!segs.length) continue;
      const pt = (edge: number): [number, number] => {
        switch (edge) {
          case 0:
            return [(i + (level - a) / (b - a)) * cs, j * cs];
          case 1:
            return [(i + 1) * cs, (j + (level - b) / (c - b)) * cs];
          case 2:
            return [(i + (level - d) / (c - d)) * cs, (j + 1) * cs];
          default:
            return [i * cs, (j + (level - a) / (d - a)) * cs];
        }
      };
      for (let k = 0; k < segs.length; k += 2) {
        const [x0, y0] = pt(segs[k]);
        const [x1, y1] = pt(segs[k + 1]);
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
      }
    }
  }
  ctx.stroke();
}

// ---- helpers ------------------------------------------------------------------------------------

type Pt = [number, number];

function chaikin(pts: Pt[], iterations = 2): Pt[] {
  let out = pts;
  for (let it = 0; it < iterations; it++) {
    const next: Pt[] = [out[0]];
    for (let i = 0; i < out.length - 1; i++) {
      const [x0, y0] = out[i];
      const [x1, y1] = out[i + 1];
      next.push(
        [x0 * 0.75 + x1 * 0.25, y0 * 0.75 + y1 * 0.25],
        [x0 * 0.25 + x1 * 0.75, y0 * 0.25 + y1 * 0.75],
      );
    }
    next.push(out[out.length - 1]);
    out = next;
  }
  return out;
}

/** Append a polyline to the current path. */
function polyline(ctx: CanvasRenderingContext2D, pts: readonly Pt[]): void {
  for (let i = 0; i < pts.length; i++) {
    if (i) ctx.lineTo(pts[i][0], pts[i][1]);
    else ctx.moveTo(pts[i][0], pts[i][1]);
  }
}

const hexRgb = (hex: string): [number, number, number] => {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const CITY_NAMES = [
  'Dornheim',
  'Kessling',
  'Varn',
  'Ostrava',
  'Brennholt',
  'Lucavy',
  'Tarskov',
  'Mirow',
  'Halden',
  'Zelnik',
  'Grauwitz',
  'Pellau',
  'Sorvan',
  'Wendisch',
];

/** Tapered operation arrow along a quadratic bezier. */
function opArrow(
  ctx: CanvasRenderingContext2D,
  from: Pt,
  ctrl: Pt,
  to: Pt,
  width: number,
  color: string,
  dashed = false,
): void {
  const N = 40;
  const pts: Pt[] = [];
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const x = (1 - t) * (1 - t) * from[0] + 2 * (1 - t) * t * ctrl[0] + t * t * to[0];
    const y = (1 - t) * (1 - t) * from[1] + 2 * (1 - t) * t * ctrl[1] + t * t * to[1];
    pts.push([x, y]);
  }
  const headLen = width * 2.6;
  // Stop the body where the head begins.
  let cut = N;
  let acc = 0;
  for (let i = N; i > 0; i--) {
    acc += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    if (acc >= headLen) {
      cut = i;
      break;
    }
  }
  const left: Pt[] = [];
  const right: Pt[] = [];
  for (let i = 0; i <= cut; i++) {
    const [x, y] = pts[i];
    const [nx0, ny0] = pts[Math.min(i + 1, N)];
    const [px0, py0] = pts[Math.max(i - 1, 0)];
    const dx = nx0 - px0;
    const dy = ny0 - py0;
    const len = Math.hypot(dx, dy) || 1;
    const w = (width / 2) * (0.35 + 0.65 * (i / cut));
    left.push([x - (dy / len) * w, y + (dx / len) * w]);
    right.push([x + (dy / len) * w, y - (dx / len) * w]);
  }
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  if (dashed) {
    ctx.lineWidth = width * 0.45;
    ctx.setLineDash([width * 0.9, width * 0.7]);
    ctx.beginPath();
    polyline(ctx, pts.slice(0, cut + 1));
    ctx.stroke();
    ctx.setLineDash([]);
  } else {
    ctx.beginPath();
    polyline(ctx, left);
    for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i][0], right[i][1]);
    ctx.closePath();
    ctx.fill();
  }
  // Head
  const [bx, by] = pts[cut];
  const dx = to[0] - bx;
  const dy = to[1] - by;
  const len = Math.hypot(dx, dy) || 1;
  const hw = width * 1.25;
  ctx.beginPath();
  ctx.moveTo(to[0], to[1]);
  ctx.lineTo(bx - (dy / len) * hw, by + (dx / len) * hw);
  ctx.lineTo(bx + (dy / len) * hw, by - (dx / len) * hw);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

const COUNTER_TONES: Record<Tone, string> = {
  ink: '#2b2620',
  mid: '#5a4f42',
  hi: '#f4ead2',
  face: '',
  flag: '#f1e7cf',
};

/** A small illustrated counter (the same silhouette art as the map, unitArt.ts). */
function counter(ctx: CanvasRenderingContext2D, x: number, y: number, fill: string, type: UnitType): void {
  const w = 30;
  const h = 20;
  ctx.save();
  ctx.translate(x, y);
  ctx.shadowColor = 'rgba(40,30,15,0.45)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.roundRect(-w / 2, -h / 2, w, h, 1.5);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = '#2b2620';
  ctx.lineWidth = 1.2;
  ctx.stroke();
  // Art box (ART_W × ART_H, 3:2) onto the counter face.
  ctx.translate(-w / 2, -h / 2);
  ctx.scale(w / ART_W, h / ART_H);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const layer of unitArt(type).layers) {
    const path = new Path2D(layer.d);
    const color = layer.tone === 'face' ? fill : COUNTER_TONES[layer.tone];
    if (layer.stroke) {
      ctx.strokeStyle = color;
      ctx.lineWidth = layer.stroke;
      ctx.stroke(path);
    } else {
      ctx.fillStyle = color;
      ctx.fill(path, layer.evenOdd ? 'evenodd' : 'nonzero');
    }
  }
  ctx.restore();
}

// ---- main ---------------------------------------------------------------------------------------

/**
 * Paint the whole staff map. `regions` are the italic province names lettered across it (in the
 * interface language — the menu repaints when it changes).
 */
export function paintStaffMap(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed: number,
  regions: readonly string[],
): void {
  const rng = new Rng(seed);
  const nElev = perlin(seed);
  const nMoist = perlin(seed ^ 0x5bd1e995);
  const nFront = perlin(seed ^ 0x1234567);

  const cs = 8;
  const cols = Math.ceil(w / cs) + 2;
  const rows = Math.ceil(h / cs) + 2;
  const scale = 1 / Math.max(420, Math.min(w, h) * 0.55);
  const elev = new Float32Array(cols * rows);
  const moist = new Float32Array(cols * rows);
  // Sea in the lower-right, high ground in the upper middle: keeps the interesting part of the
  // map to the right of the menu sidebar.
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const x = i * cs;
      const y = j * cs;
      const sea = Math.max(0, Math.hypot((x - w * 1.02) / w, (y - h * 1.05) / h) * -1 + 0.62) * 1.1;
      const ridge = Math.max(0, 0.18 - Math.abs(y / h - 0.22)) * 0.9;
      elev[j * cols + i] = fbm(nElev, x * scale * 2.2, y * scale * 2.2) - sea + ridge;
      moist[j * cols + i] = fbm(nMoist, x * scale * 3, y * scale * 3, 4);
    }
  }
  const SEA = 0.36;
  const frontX = (y: number) => w * 0.62 + (fbm(nFront, 3.1, y * scale * 2.5, 3) - 0.5) * w * 0.34;

  // 1. Colour field at low resolution, upscaled with smoothing (soft washes like watercolour).
  const tint = document.createElement('canvas');
  tint.width = cols;
  tint.height = rows;
  const tctx = tint.getContext('2d')!;
  const img = tctx.createImageData(cols, rows);
  const C = {
    plains: hexRgb('#d8cfa9'),
    dry: hexRgb('#dccb98'),
    wet: hexRgb('#c9cda0'),
    forest: hexRgb('#aab38a'),
    hills: hexRgb('#cfc29a'),
    mount: hexRgb('#bfae8d'),
    peak: hexRgb('#e9e1cf'),
    shallow: hexRgb('#a9c1c4'),
    deep: hexRgb('#86a6b3'),
    red: hexRgb('#b23a2e'),
    blue: hexRgb('#2f5d8a'),
  };
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const k = j * cols + i;
      const e = elev[k];
      const m = moist[k];
      let c: [number, number, number];
      if (e < SEA) c = e < SEA - 0.07 ? C.deep : C.shallow;
      else if (e > 0.8) c = C.peak;
      else if (e > 0.7) c = C.mount;
      else if (e > 0.6) c = C.hills;
      else if (m > 0.57) c = C.forest;
      else if (m > 0.52) c = C.wet;
      else if (m < 0.44) c = C.dry;
      else c = C.plains;
      let [r, g, b] = c;
      if (e >= SEA) {
        const f = i * cs < frontX(j * cs) ? C.red : C.blue;
        r = r * 0.84 + f[0] * 0.16;
        g = g * 0.84 + f[1] * 0.16;
        b = b * 0.84 + f[2] * 0.16;
      }
      img.data[k * 4] = r;
      img.data[k * 4 + 1] = g;
      img.data[k * 4 + 2] = b;
      img.data[k * 4 + 3] = 255;
    }
  }
  tctx.putImageData(img, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(tint, 0, 0, cols * cs, rows * cs);

  // 2. Forest stipple.
  ctx.fillStyle = 'rgba(95, 110, 70, 0.5)';
  for (let n = 0; n < (w * h) / 260; n++) {
    const x = rng.range(0, w);
    const y = rng.range(0, h);
    const k = Math.floor(y / cs) * cols + Math.floor(x / cs);
    if (moist[k] > 0.585 && elev[k] >= SEA + 0.02 && elev[k] < 0.6) {
      ctx.beginPath();
      ctx.arc(x, y, 1.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // 3. Engraved water lines, contours, coastline.
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgba(79, 125, 147, 0.35)';
  ctx.lineWidth = 0.8;
  for (const d of [0.018, 0.036, 0.056, 0.08]) traceContour(ctx, elev, cols, rows, cs, SEA - d);
  let n = 0;
  for (let lv = SEA + 0.045; lv < 0.95; lv += 0.045, n++) {
    ctx.strokeStyle = n % 4 === 3 ? 'rgba(120, 100, 70, 0.55)' : 'rgba(140, 122, 90, 0.34)';
    ctx.lineWidth = n % 4 === 3 ? 1.1 : 0.7;
    traceContour(ctx, elev, cols, rows, cs, lv);
  }
  ctx.strokeStyle = 'rgba(55, 80, 92, 0.85)';
  ctx.lineWidth = 1.5;
  traceContour(ctx, elev, cols, rows, cs, SEA);

  // 4. Rivers: steepest descent from high ground to the sea, smoothed.
  ctx.strokeStyle = '#4f7d93';
  for (let r = 0; r < 4; r++) {
    let i = 0;
    let j = 0;
    for (let tries = 0; tries < 200; tries++) {
      i = rng.int(2, cols - 3);
      j = rng.int(2, rows - 3);
      if (elev[j * cols + i] > 0.62) break;
    }
    const pts: Pt[] = [[i * cs, j * cs]];
    for (let s = 0; s < 400 && elev[j * cols + i] >= SEA; s++) {
      let best = elev[j * cols + i];
      let bi = i;
      let bj = j;
      for (let dj = -1; dj <= 1; dj++)
        for (let di = -1; di <= 1; di++) {
          const ni = i + di;
          const nj = j + dj;
          if (ni < 0 || nj < 0 || ni >= cols || nj >= rows) continue;
          const v = elev[nj * cols + ni] + rng.range(0, 0.004);
          if (v < best) {
            best = v;
            bi = ni;
            bj = nj;
          }
        }
      if (bi === i && bj === j) {
        // Local pit: nudge towards the sea.
        bi = Math.min(cols - 1, i + 1);
        bj = Math.min(rows - 1, j + 1);
      }
      i = bi;
      j = bj;
      pts.push([i * cs, j * cs]);
    }
    const smooth = chaikin(
      pts.filter((_, k) => k % 2 === 0),
      3,
    );
    for (let k = 1; k < smooth.length; k++) {
      ctx.lineWidth = 0.8 + (k / smooth.length) * 2.4;
      ctx.beginPath();
      ctx.moveTo(smooth[k - 1][0], smooth[k - 1][1]);
      ctx.lineTo(smooth[k][0], smooth[k][1]);
      ctx.stroke();
    }
  }

  // 5. Faint hex grid.
  const R = 34;
  const hw = Math.sqrt(3) * R;
  ctx.strokeStyle = 'rgba(43, 38, 32, 0.07)';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  for (let row = -1; row * R * 1.5 < h + R; row++) {
    for (let col = -1; col * hw < w + hw; col++) {
      const cx = col * hw + (row & 1 ? hw / 2 : 0);
      const cy = row * R * 1.5;
      for (let c = 0; c < 3; c++) {
        const a0 = (Math.PI / 180) * (60 * c - 30);
        const a1 = (Math.PI / 180) * (60 * (c + 1) - 30);
        ctx.moveTo(cx + R * Math.cos(a0), cy + R * Math.sin(a0));
        ctx.lineTo(cx + R * Math.cos(a1), cy + R * Math.sin(a1));
      }
    }
  }
  ctx.stroke();

  // 6. Front line with teeth pointing at the enemy (east).
  const front: Pt[] = [];
  for (let y = -10; y <= h + 10; y += 12) {
    const k =
      Math.max(0, Math.min(rows - 1, Math.floor(y / cs))) * cols +
      Math.max(0, Math.min(cols - 1, Math.floor(frontX(y) / cs)));
    if (elev[k] >= SEA) front.push([frontX(y), y]);
    else if (front.length) break;
  }
  if (front.length > 2) {
    ctx.strokeStyle = 'rgba(60, 18, 14, 0.85)';
    ctx.lineWidth = 3.2;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    polyline(ctx, front);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(178, 58, 46, 0.9)';
    ctx.lineWidth = 1.4;
    ctx.stroke();
    ctx.fillStyle = 'rgba(60, 18, 14, 0.85)';
    for (let i = 1; i < front.length - 1; i += 2) {
      const [x0, y0] = front[i - 1];
      const [x1, y1] = front[i + 1];
      const [x, y] = front[i];
      const dx = x1 - x0;
      const dy = y1 - y0;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;
      const sx = nx < 0 ? -1 : 1; // teeth face east
      ctx.beginPath();
      ctx.moveTo(x - (dx / len) * 4, y - (dy / len) * 4);
      ctx.lineTo(x + (dx / len) * 4, y + (dy / len) * 4);
      ctx.lineTo(x + nx * sx * 6, y + ny * sx * 6);
      ctx.closePath();
      ctx.fill();
    }
  }

  // 7. Operation arrows across the front.
  const fy = (t: number) => h * t;
  opArrow(
    ctx,
    [frontX(fy(0.34)) - w * 0.16, fy(0.4)],
    [frontX(fy(0.3)) - w * 0.02, fy(0.24)],
    [frontX(fy(0.3)) + w * 0.12, fy(0.33)],
    16,
    'rgba(178, 58, 46, 0.55)',
  );
  opArrow(
    ctx,
    [frontX(fy(0.62)) - w * 0.14, fy(0.64)],
    [frontX(fy(0.58)) + w * 0.02, fy(0.72)],
    [frontX(fy(0.55)) + w * 0.1, fy(0.56)],
    12,
    'rgba(178, 58, 46, 0.5)',
  );
  opArrow(
    ctx,
    [frontX(fy(0.46)) + w * 0.18, fy(0.5)],
    [frontX(fy(0.47)) + w * 0.08, fy(0.44)],
    [frontX(fy(0.47)) + w * 0.02, fy(0.47)],
    10,
    'rgba(47, 93, 138, 0.6)',
    true,
  );

  // 8. Cities and names.
  const placed: Pt[] = [];
  const names = rng.shuffle([...CITY_NAMES]);
  for (let tries = 0; tries < 400 && placed.length < 12; tries++) {
    const x = rng.range(w * 0.28, w * 0.97);
    const y = rng.range(h * 0.06, h * 0.94);
    const k = Math.floor(y / cs) * cols + Math.floor(x / cs);
    if (elev[k] < SEA + 0.02 || elev[k] > 0.7) continue;
    if (placed.some(([px, py]) => Math.hypot(px - x, py - y) < 150)) continue;
    placed.push([x, y]);
    const size = placed.length <= 2 ? 3 : placed.length <= 5 ? 2 : 1;
    ctx.fillStyle = '#2b2620';
    ctx.strokeStyle = '#2b2620';
    ctx.lineWidth = 1.2;
    if (size === 3) {
      ctx.fillRect(x - 5, y - 5, 10, 10);
      ctx.strokeRect(x - 8.5, y - 8.5, 17, 17);
    } else if (size === 2) ctx.fillRect(x - 4, y - 4, 8, 8);
    else {
      ctx.beginPath();
      ctx.arc(x, y, 3.2, 0, Math.PI * 2);
      ctx.fill();
    }
    const name = names[placed.length - 1];
    ctx.font =
      size === 3
        ? '600 17px "EB Garamond", serif'
        : size === 2
          ? '500 15px "EB Garamond", serif'
          : 'italic 13px "EB Garamond", serif';
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(43, 38, 32, 0.9)';
    ctx.fillText(size >= 2 ? name.toUpperCase().split('').join('\u200A') : name, x + 11, y + 5);
  }

  // 9. Region labels.
  ctx.fillStyle = 'rgba(43, 38, 32, 0.38)';
  ctx.textAlign = 'center';
  ctx.font = `italic ${Math.round(Math.min(34, w / 48))}px "EB Garamond", serif`;
  rng
    .shuffle([...regions])
    .slice(0, 2)
    .forEach((label, i) => {
      ctx.save();
      ctx.translate(w * (0.5 + i * 0.3), h * (0.18 + i * 0.58));
      ctx.rotate(-0.06 + i * 0.1);
      ctx.fillText(label.split('').join('\u2009'), 0, 0);
      ctx.restore();
    });

  // 10. Counters on both sides of the front.
  for (let i = 0; i < 7; i++) {
    const y = h * (0.2 + i * 0.1) + rng.range(-10, 10);
    const side = i % 2 === 0 ? -1 : 1;
    const x = frontX(y) + side * rng.range(26, 60);
    const k = Math.floor(y / cs) * cols + Math.floor(x / cs);
    if (elev[k] < SEA) continue;
    counter(
      ctx,
      x,
      y,
      side < 0 ? '#d9a39b' : '#9fb3c8',
      rng.pick(['infantry', 'infantry', 'armor', 'artillery'] as const),
    );
  }
}
